/**
 * Player identity utilities for persistent player identification
 * Matches original game's player_hash system (cfgfile.cc:224-235)
 *
 * The original game generates a hash from player name + password stored in config.
 * For web, we use playerName + persistent UUID stored in localStorage.
 */

/**
 * Generate or retrieve a persistent connection ID (UUID v4)
 * Stored in localStorage so it persists across page refreshes
 * This is used for player identity/hash generation, not for network connection IDs
 */
export function getConnectionId(): string {
  const STORAGE_KEY = "quadra_connectionId";

  let connectionId = localStorage.getItem(STORAGE_KEY);

  if (!connectionId) {
    // Generate new UUID v4
    connectionId = generateUUID();
    localStorage.setItem(STORAGE_KEY, connectionId);
  }

  return connectionId;
}

/**
 * @deprecated Use getConnectionId() instead
 * Kept for backward compatibility
 */
export function getPlayerId(): string {
  return getConnectionId();
}

/**
 * Generate a player hash from player name + player ID
 * Uses SHA-256 and takes first 16 bytes (matching original's 16-byte hash)
 * Returns hex-encoded string (32 characters)
 */
export async function generatePlayerHash(
  playerName: string,
  playerId: string
): Promise<string> {
  const data = `${playerName}:${playerId}`;

  // Use Web Crypto API for SHA-256
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);

  // Take first 16 bytes (matching original's player_hash[16])
  const hashArray = new Uint8Array(hashBuffer).slice(0, 16);

  // Convert to hex string
  return Array.from(hashArray)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Generate player hash synchronously (for cases where async isn't available)
 * Uses a simple hash function - less secure but works everywhere
 */
export function generatePlayerHashSync(
  playerName: string,
  playerId: string
): string {
  const data = `${playerName}:${playerId}`;
  let hash = 0;

  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to 16-byte hex string (32 chars)
  const hashBytes = new Uint8Array(16);
  const hashStr = Math.abs(hash).toString(16).padStart(32, "0");

  for (let i = 0; i < 16; i++) {
    hashBytes[i] = parseInt(hashStr.substr(i * 2, 2), 16);
  }

  return Array.from(hashBytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Store game connection info for auto-rejoin on page refresh
 */
export function storeGameConnection(hostId: string): void {
  localStorage.setItem("lastGameHostId", hostId);
  localStorage.setItem("lastGameTimestamp", Date.now().toString());
}

/**
 * Retrieve stored game connection info
 * Returns null if no connection stored or if it's too old (> 5 minutes)
 */
export function getStoredGameConnection(): string | null {
  const hostId = localStorage.getItem("lastGameHostId");
  const timestampStr = localStorage.getItem("lastGameTimestamp");

  if (!hostId || !timestampStr) {
    return null;
  }

  const timestamp = parseInt(timestampStr, 10);
  const age = Date.now() - timestamp;
  const MAX_AGE = 5 * 60 * 1000; // 5 minutes

  if (age > MAX_AGE) {
    // Too old, clear it
    clearGameConnection();
    return null;
  }

  return hostId;
}

/**
 * Clear stored game connection info
 */
export function clearGameConnection(): void {
  localStorage.removeItem("lastGameHostId");
  localStorage.removeItem("lastGameTimestamp");
}
